import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Trash2, Search, X, Building2, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";

const TONE_CLASS = {
  emerald: "text-emerald-800 bg-emerald-100 border-emerald-300",
  indigo:  "text-indigo-800 bg-indigo-100 border-indigo-300",
  amber:   "text-amber-800 bg-amber-100 border-amber-300",
  rose:    "text-rose-800 bg-rose-100 border-rose-300",
  zinc:    "text-zinc-700 bg-zinc-100 border-zinc-200",
};

const STATUS_TONE = {
  operational: "emerald",
  new:         "indigo",
  closing:     "amber",
  closed:      "rose",
  pre_launch:  "amber",
};

const STATUS_LABELS = {
  operational: "Operational",
  new:         "New",
  closing:     "Closing",
  closed:      "Closed",
  pre_launch:  "Pre-launch",
};

export default function LifecyclePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingLoc, setSavingLoc] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  // Local edits keyed by location so user can stage many changes before saving
  const [edits, setEdits] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lifecycle");
      setData(data);
      setEdits({});
    } catch (e) {
      logError("admin lifecycle load failed", e);
      toast.error("Could not load lifecycle data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setEdit = (loc, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [loc]: { ...(prev[loc] || {}), [field]: value },
    }));
  };

  const filteredProps = useMemo(() => {
    if (!data?.properties) return [];
    let arr = data.properties;
    if (filter !== "all") arr = arr.filter((p) => p.lifecycle_status === filter);
    const term = q.trim().toLowerCase();
    if (term) arr = arr.filter((p) => p.location.toLowerCase().includes(term));
    return arr;
  }, [data, filter, q]);

  const counts = useMemo(() => {
    const c = { all: 0, operational: 0, new: 0, closing: 0, closed: 0, pre_launch: 0 };
    (data?.properties || []).forEach((p) => {
      c.all++;
      c[p.lifecycle_status] = (c[p.lifecycle_status] || 0) + 1;
    });
    return c;
  }, [data]);

  const saveOverride = async (loc) => {
    const orig = data.properties.find((p) => p.location === loc);
    const local = edits[loc] || {};
    const status = local.status ?? orig.lifecycle_status;
    const payload = {
      location: loc,
      status,
      closing_on: local.closing_on ?? orig.closing_on ?? null,
      closed_on: local.closed_on ?? orig.closed_on ?? null,
      notes: local.notes ?? orig.notes ?? null,
    };
    setSavingLoc(loc);
    try {
      await api.post("/admin/lifecycle", payload);
      toast.success(`${loc} → ${STATUS_LABELS[status]}`);
      await load();
    } catch (e) {
      logError("save lifecycle failed", e);
      toast.error("Save failed");
    } finally { setSavingLoc(null); }
  };

  const deleteOverride = async (loc) => {
    if (!confirm(`Remove override for ${loc}? It will fall back to seed or auto-derive.`)) return;
    setSavingLoc(loc);
    try {
      await api.delete(`/admin/lifecycle/${encodeURIComponent(loc)}`);
      toast.success(`${loc} override removed`);
      await load();
    } catch (e) {
      logError("delete lifecycle failed", e);
      toast.error("Delete failed");
    } finally { setSavingLoc(null); }
  };

  if (loading || !data) return (
    <div className="py-16 text-center text-zinc-500">
      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-lifecycle">
      <header>
        <h2 className="font-display font-black text-2xl tracking-tight">Property Lifecycle</h2>
        <p className="text-[12px] text-zinc-500 mt-0.5">
          Flag properties as <span className="font-bold">New / Closing / Closed / Pre-launch</span> so the MIS Analysis dashboards label them correctly. Stays in MongoDB · audited.
        </p>
      </header>

      {/* Filter + search */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="lifecycle-filter">
          {[
            { key: "all",         label: `All · ${counts.all}` },
            { key: "operational", label: `Operational · ${counts.operational}` },
            { key: "new",         label: `New · ${counts.new || 0}` },
            { key: "closing",     label: `Closing · ${counts.closing || 0}` },
            { key: "closed",      label: `Closed · ${counts.closed || 0}` },
            { key: "pre_launch",  label: `Pre-launch · ${counts.pre_launch || 0}` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`lifecycle-filter-${f.key}`}
              className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                filter === f.key
                  ? "bg-teal-700 text-white border-teal-700 shadow-sm"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find property…"
            className="pl-7 h-8 w-64 text-[12px]"
            data-testid="lifecycle-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-2 text-zinc-400">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[9.5px] uppercase tracking-wider text-zinc-500">
                <th className="py-2.5 px-3">Property</th>
                <th className="py-2.5 px-2">Current</th>
                <th className="py-2.5 px-2">Source</th>
                <th className="py-2.5 px-2">Status (edit)</th>
                <th className="py-2.5 px-2">Closing on</th>
                <th className="py-2.5 px-2">Closed on</th>
                <th className="py-2.5 px-2">Notes</th>
                <th className="py-2.5 px-2 text-right">Save / Reset</th>
              </tr>
            </thead>
            <tbody>
              {filteredProps.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-zinc-400">No matching properties.</td></tr>
              ) : filteredProps.map((p) => {
                const local = edits[p.location] || {};
                const currentStatus = local.status ?? p.lifecycle_status;
                const dirty = Object.keys(local).length > 0;
                const tone = STATUS_TONE[p.lifecycle_status] || "zinc";
                const showClosing = currentStatus === "closing";
                const showClosed = currentStatus === "closed";
                return (
                  <tr key={p.location} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-zinc-900 leading-tight flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 text-zinc-400" />
                        {p.location}
                      </div>
                      <div className="text-[10px] text-zinc-500 leading-tight">
                        {p.type} · {p.model}
                        {p.start_date && <span> · start {p.start_date.slice(0, 7)}</span>}
                        {p.last_revenue_month && <span> · last rev {p.last_revenue_month}</span>}
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <span className={`inline-block text-[9.5px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded ${TONE_CLASS[tone]}`}>
                        {p.lifecycle_label}
                      </span>
                      {p.lifecycle_sub && (
                        <div className="text-[10px] text-zinc-500 mt-0.5 italic">{p.lifecycle_sub}</div>
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-[10px] text-zinc-500 uppercase tracking-wider">{p.source}</td>
                    <td className="py-2.5 px-2">
                      <select
                        value={currentStatus}
                        onChange={(e) => setEdit(p.location, "status", e.target.value)}
                        data-testid={`lifecycle-status-${p.location}`}
                        className="text-[11px] border border-zinc-200 rounded px-1.5 py-1 bg-white hover:border-teal-400 focus:outline-none focus:border-teal-500"
                      >
                        {data.valid_statuses.map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2.5 px-2">
                      {showClosing ? (
                        <Input
                          type="month"
                          value={local.closing_on ?? p.closing_on ?? ""}
                          onChange={(e) => setEdit(p.location, "closing_on", e.target.value)}
                          className="h-7 w-32 text-[11px]"
                          data-testid={`lifecycle-closing-${p.location}`}
                        />
                      ) : <span className="text-[10px] text-zinc-300">—</span>}
                    </td>
                    <td className="py-2.5 px-2">
                      {showClosed ? (
                        <Input
                          type="month"
                          value={local.closed_on ?? p.closed_on ?? ""}
                          onChange={(e) => setEdit(p.location, "closed_on", e.target.value)}
                          className="h-7 w-32 text-[11px]"
                          data-testid={`lifecycle-closed-${p.location}`}
                        />
                      ) : <span className="text-[10px] text-zinc-300">—</span>}
                    </td>
                    <td className="py-2.5 px-2">
                      <Input
                        type="text"
                        value={local.notes ?? p.notes ?? ""}
                        onChange={(e) => setEdit(p.location, "notes", e.target.value)}
                        placeholder="e.g. Lease ends Q1"
                        className="h-7 w-48 text-[11px]"
                        data-testid={`lifecycle-notes-${p.location}`}
                      />
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => saveOverride(p.location)}
                          disabled={savingLoc === p.location || !dirty}
                          data-testid={`lifecycle-save-${p.location}`}
                          className={`text-[10.5px] px-2 py-1 rounded border font-bold inline-flex items-center gap-1 ${
                            dirty ? "bg-teal-700 text-white border-teal-700 hover:bg-teal-800" : "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"
                          }`}
                        >
                          {savingLoc === p.location ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          Save
                        </button>
                        {p.source === "runtime" && (
                          <button
                            onClick={() => deleteOverride(p.location)}
                            disabled={savingLoc === p.location}
                            data-testid={`lifecycle-delete-${p.location}`}
                            className="text-[10.5px] px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                            title="Remove override; revert to seed or auto-derive"
                          >
                            <Trash2 className="w-3 h-3" /> Reset
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10.5px] text-zinc-500 flex items-start gap-1.5 max-w-2xl">
        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          <span className="font-bold">Source</span>: <span className="font-bold uppercase tracking-wider">runtime</span> = saved by an admin (MongoDB) ·
          <span className="font-bold uppercase tracking-wider"> seed</span> = bootstrapped from code ·
          <span className="font-bold uppercase tracking-wider"> auto</span> = derived from start_date + MIS data.
          Changes propagate instantly to all MIS dashboards.
        </span>
      </div>
    </div>
  );
}
